import { UpgradeModel } from "types";

import academyCoreImage from "assets/upgrade-academy-core.svg";
import academyLaserImage from "assets/upgrade-academy-laser.svg";
import academyScopeImage from "assets/upgrade-academy-scope.svg";
import academyShieldImage from "assets/upgrade-academy-shield.svg";
import academyThrusterImage from "assets/upgrade-academy-thruster.svg";

import {
  attackAnimation,
  reactorCoreAnimation,
  shieldsAnimation,
  targetingSystemAnimation,
  thrusterAnimation,
} from "components/styles/animations";
import {
  DEFAULT_REACTOR_SPEED,
  DEFAULT_SHIELDS_SPEED,
  DEFAULT_TARGETING_SPEED,
  DEFAULT_THRUSTERS_SPEED,
  DEFAULT_WEAPONS_SPEED,
} from "../constants";

const BISHOP_LAUNCH_CHECK_UPGRADES: Record<string, UpgradeModel> = {
  hyperion_academy_laser: {
    slug: "hyperion_academy_laser",
    name: "Hyperion Academy Laser",
    description:
      "An academy issued laser built for the Hyperion starship class.",
    image: academyLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
    isTutorial: true,
  },
  hyperion_academy_shield: {
    slug: "hyperion_academy_shield",
    name: "Hyperion Academy Shield",
    description:
      "An academy issued shield built for the Hyperion starship class.",
    image: academyShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
    isTutorial: true,
  },
  hyperion_academy_thruster: {
    slug: "hyperion_academy_thruster",
    name: "Hyperion Academy Thruster",
    description:
      "An academy issued thruster built for the Hyperion starship class.",
    image: academyThrusterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
    isTutorial: true,
  },
  hyperion_academy_scope: {
    slug: "hyperion_academy_scope",
    name: "Hyperion Academy Scope",
    description:
      "An academy issued targeting system built for the Hyperion starship class.",
    image: academyScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 1,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
    isTutorial: true,
  },
  hyperion_academy_core: {
    slug: "hyperion_academy_core",
    name: "Hyperion Academy Core",
    description:
      "An academy issued reactor core built for the Hyperion starship class.",
    image: academyCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 2,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
    isTutorial: true,
  },
  nix_academy_laser: {
    slug: "nix_academy_laser",
    name: "Nix Academy Laser",
    description: "An academy issued laser built for the Nix starship class.",
    image: academyLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 2,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
    isTutorial: true,
  },
  nix_academy_shield: {
    slug: "nix_academy_shield",
    name: "Nix Academy Shield",
    description: "An academy issued shield built for the Nix starship class.",
    image: academyShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 1,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
    isTutorial: true,
  },
  nix_academy_thruster: {
    slug: "nix_academy_thruster",
    name: "Nix Academy Thruster",
    description: "An academy issued thruster built for the Nix starship class.",
    image: academyThrusterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
    isTutorial: true,
  },
  nix_academy_scope: {
    slug: "nix_academy_scope",
    name: "Nix Academy Scope",
    description:
      "An academy issued targeting system built for the Nix starship class.",
    image: academyScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
    isTutorial: true,
  },
  nix_academy_core: {
    slug: "nix_academy_core",
    name: "Nix Academy Core",
    description:
      "An academy issued reactor core built for the Nix starship class.",
    image: academyCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
    isTutorial: true,
  },
  titania_academy_laser: {
    slug: "titania_academy_laser",
    name: "Titania Academy Laser",
    description:
      "An academy issued laser built for the Titania starship class.",
    image: academyLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
    isTutorial: true,
  },
  titania_academy_shield: {
    slug: "titania_academy_shield",
    name: "Titania Academy Shield",
    description:
      "An academy issued shield built for the Titania starship class.",
    image: academyShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
    isTutorial: true,
  },
  titania_academy_thruster: {
    slug: "titania_academy_thruster",
    name: "Titania Academy Thruster",
    description:
      "An academy issued thruster built for the Titania starship class.",
    image: academyThrusterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 1,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
    isTutorial: true,
  },
  titania_academy_scope: {
    slug: "titania_academy_scope",
    name: "Titania Academy Scope",
    description:
      "An academy issued targeting system built for the Titania starship class.",
    image: academyScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 2,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
    isTutorial: true,
  },
  titania_academy_core: {
    slug: "titania_academy_core",
    name: "Titania Academy Core",
    description:
      "An academy issued reactor core built for the Titania starship class.",
    image: academyCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
    isTutorial: true,
  },
  calypso_academy_laser: {
    slug: "calypso_academy_laser",
    name: "Calypso Academy Laser",
    description:
      "An academy issued laser built for the Calypso starship class.",
    image: academyLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 1,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
    isTutorial: true,
  },
  calypso_academy_shield: {
    slug: "calypso_academy_shield",
    name: "Calypso Academy Shield",
    description:
      "An academy issued shield built for the Calypso starship class.",
    image: academyShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 2,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
    isTutorial: true,
  },
  calypso_academy_thruster: {
    slug: "calypso_academy_thruster",
    name: "Calypso Academy Thruster",
    description:
      "An academy issued thruster built for the Calypso starship class.",
    image: academyThrusterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
    isTutorial: true,
  },
  calypso_academy_scope: {
    slug: "calypso_academy_scope",
    name: "Calypso Academy Scope",
    description:
      "An academy issued targeting system built for the Calypso starship class.",
    image: academyScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
    isTutorial: true,
  },
  calypso_academy_core: {
    slug: "calypso_academy_core",
    name: "Calypso Academy Core",
    description:
      "An academy issued reactor core built for the Calypso starship class.",
    image: academyCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
    isTutorial: true,
  },
  atlas_academy_laser: {
    slug: "atlas_academy_laser",
    name: "Atlas Academy Laser",
    description: "An academy issued laser built for the Atlas starship class.",
    image: academyLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
    isTutorial: true,
  },
  atlas_academy_shield: {
    slug: "atlas_academy_shield",
    name: "Atlas Academy Shield",
    description: "An academy issued shield built for the Atlas starship class.",
    image: academyShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
    isTutorial: true,
  },
  atlas_academy_thruster: {
    slug: "atlas_academy_thruster",
    name: "Atlas Academy Thruster",
    description:
      "An academy issued thruster built for the Atlas starship class.",
    image: academyThrusterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 2,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
    isTutorial: true,
  },
  atlas_academy_scope: {
    slug: "atlas_academy_scope",
    name: "Atlas Academy Scope",
    description:
      "An academy issued targeting system built for the Atlas starship class.",
    image: academyScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
    isTutorial: true,
  },
  atlas_academy_core: {
    slug: "atlas_academy_core",
    name: "Atlas Academy Core",
    description:
      "An academy issued reactor core built for the Atlas starship class.",
    image: academyCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 1,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
    isTutorial: true,
  },
};

export default BISHOP_LAUNCH_CHECK_UPGRADES;
